/**
 * The island's sounds: tiny, soft, and made on the fly with Web Audio, so there are no files to
 * load and nothing sounds like a stock system bell.
 *
 * Each sound is one or two short sine/triangle notes through a gentle low-pass filter, with a
 * fast attack and a quick exponential tail (the "glassy tick" feel of iOS UI sounds). Volumes
 * are deliberately low. Browsers only allow audio after the person has interacted with the page,
 * so the first sounds may be silent until then; that is fine.
 */

export type IslandSound = 'open' | 'close' | 'tap' | 'alert' | 'success' | 'warning' | 'brief' | 'pickup' | 'drop'

let ctx: AudioContext | null = null

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  ctx ??= new Ctor()
  if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
  return ctx
}

interface Note {
  freq: number
  /** Start offset in seconds. */
  at?: number
  dur?: number
  gain?: number
  type?: OscillatorType
  /** Glide to this frequency over the note (a tiny pitch bend reads as "physical"). */
  to?: number
}

function play(notes: Note[], cutoff = 4_000): void {
  const ac = audio()
  if (!ac) return
  const t0 = ac.currentTime + 0.005
  const filter = ac.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = cutoff
  filter.connect(ac.destination)
  for (const n of notes) {
    const osc = ac.createOscillator()
    const g = ac.createGain()
    const start = t0 + (n.at ?? 0)
    const dur = n.dur ?? 0.12
    osc.type = n.type ?? 'sine'
    osc.frequency.setValueAtTime(n.freq, start)
    if (n.to) osc.frequency.exponentialRampToValueAtTime(n.to, start + dur)
    g.gain.setValueAtTime(0.0001, start)
    g.gain.exponentialRampToValueAtTime(n.gain ?? 0.05, start + 0.008)
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur)
    osc.connect(g).connect(filter)
    osc.start(start)
    osc.stop(start + dur + 0.02)
  }
}

const SOUNDS: Record<IslandSound, () => void> = {
  // Opening: bright and rising. Two quick sine notes a major sixth apart with a high sparkle on
  // top, so it reads as "something unfolding".
  open: () => play([
    { freq: 659.25, dur: 0.09, gain: 0.04 },
    { freq: 1108.73, at: 0.055, dur: 0.14, gain: 0.038 },
    { freq: 2217.46, at: 0.07, dur: 0.06, gain: 0.008 },
  ], 6_000),
  // Closing: low, short and muted. One soft tap that falls a touch, through a darker filter,
  // like something settling into place. Deliberately nothing like the open sound.
  close: () => play([{ freq: 294, to: 247, dur: 0.075, gain: 0.05 }], 1_400),
  // A glassy tick for taps on rows.
  tap: () => play([{ freq: 1800, dur: 0.035, gain: 0.025 }], 6_000),
  // Two rising notes a fifth apart: something arrived.
  alert: () => play([{ freq: 880, dur: 0.16, gain: 0.05 }, { freq: 1318.5, at: 0.09, dur: 0.22, gain: 0.045 }]),
  success: () => play([{ freq: 784, dur: 0.12, gain: 0.045 }, { freq: 1046.5, at: 0.08, dur: 0.12, gain: 0.04 }, { freq: 1568, at: 0.16, dur: 0.2, gain: 0.03 }]),
  // A gentle falling minor third: attention, not alarm.
  warning: () => play([{ freq: 740, dur: 0.16, gain: 0.05, type: 'triangle' }, { freq: 622, at: 0.12, dur: 0.24, gain: 0.045, type: 'triangle' }]),
  // A soft major chord, arpeggiated: the brief.
  brief: () => play([
    { freq: 523.25, dur: 0.5, gain: 0.03 }, { freq: 659.25, at: 0.06, dur: 0.5, gain: 0.028 },
    { freq: 783.99, at: 0.12, dur: 0.55, gain: 0.026 }, { freq: 1046.5, at: 0.18, dur: 0.6, gain: 0.02 },
  ], 3_200),
  // Lift and set down, for dragging.
  pickup: () => play([{ freq: 300, to: 620, dur: 0.1, gain: 0.04, type: 'triangle' }]),
  drop: () => play([{ freq: 420, to: 210, dur: 0.12, gain: 0.05, type: 'triangle' }, { freq: 1400, at: 0.02, dur: 0.04, gain: 0.012 }]),
}

let enabled = true
export function setIslandSoundsEnabled(on: boolean): void {
  enabled = on
}

export function playIslandSound(sound: IslandSound): void {
  if (!enabled) return
  try {
    SOUNDS[sound]()
  } catch {
    // Audio is decoration; never let it break anything.
  }
}
